import { createContext } from 'react';

/** The words every confirmation uses, whatever its tier. */
interface ConfirmCopy {
  /** The question, as the dialog's heading: "Remove Jane Okonkwo?". */
  readonly title: string;
  /** What happens if they go ahead, in the reader's terms. One or two sentences. */
  readonly consequence: string;
  /** The confirm button, named for the action: "Remove customer", never "OK" or "Yes". */
  readonly confirmLabel: string;
  /** The way out. Defaults to "Cancel". */
  readonly cancelLabel?: string;
}

/** An action that cannot be quietly undone: the consequence is stated, then confirmed by name. */
export interface DestructiveConfirmRequest extends ConfirmCopy {
  readonly tier: 'destructive';
}

/** An action that ends something for good: the consequence, and a reason that is recorded. */
export interface TerminalConfirmRequest extends ConfirmCopy {
  readonly tier: 'terminal';
  /** The reason field's label, phrased as a question: "Why is it being withdrawn?". */
  readonly reasonLabel?: string;
  /** Guidance under the reason field. */
  readonly reasonHint?: string;
}

export type ConfirmRequest = DestructiveConfirmRequest | TerminalConfirmRequest;

/**
 * Asks, and resolves with the answer.
 *
 * A destructive request resolves `true` when confirmed and `false` otherwise. A
 * terminal request resolves with the trimmed reason when confirmed and `null`
 * otherwise, so the caller cannot proceed without one.
 */
export interface ConfirmFunction {
  (request: DestructiveConfirmRequest): Promise<boolean>;
  (request: TerminalConfirmRequest): Promise<string | null>;
}

export const ConfirmContext = createContext<ConfirmFunction | null>(null);

let activeHost: ConfirmFunction | null = null;

/**
 * Makes the mounted provider reachable from code that is not a component.
 *
 * @param host the provider's ask function
 * @returns what to call when the provider unmounts
 */
export function registerConfirmHost(host: ConfirmFunction): () => void {
  activeHost = host;
  return () => {
    if (activeHost === host) {
      activeHost = null;
    }
  };
}

/**
 * Asks for confirmation from code with no component to hang a hook on, such as
 * the sign-out guard. Components use `useConfirm` instead.
 *
 * @param request what to ask
 * @returns the answer, as for the function a hook returns
 * @throws Error when no ConfirmProvider is mounted, which is a wiring mistake
 */
export function askConfirmation(request: DestructiveConfirmRequest): Promise<boolean>;
export function askConfirmation(request: TerminalConfirmRequest): Promise<string | null>;
export function askConfirmation(request: ConfirmRequest): Promise<boolean | string | null> {
  if (activeHost === null) {
    return Promise.reject(new Error('askConfirmation needs a ConfirmProvider to be mounted'));
  }
  // Narrowed so each branch matches one overload; the two calls do the same thing.
  return request.tier === 'terminal' ? activeHost(request) : activeHost(request);
}
